/**
 * 국가 표시 이름 — **브라우저 내장 `Intl.DisplayNames` 영어**로 통일한다 (#5839).
 *
 * ## 왜 mgmt 이름을 쓰지 않나
 *
 * 종전엔 mgmt `/api/v1/shipping/countries` 의 `ownCountryName` 을 행 라벨로 썼는데, 이 필드는
 * 기준이 섞여 있었다(2026-09-29 실측 · 95개국):
 *
 * ```
 *   대부분 영어        Albania · Japan · Saudi Arabia
 *   일부만 현지어      Deutschland · 대한민국 · 中国大陆 · 台灣
 *   틀린 언어          BE=Belgien(독일어) · ES=Espanya(카탈루냐어) · LU=Luxemburg(독일어)
 * ```
 *
 * 그리고 GE 모드에서 새로 들어오는 ~110개국은 mgmt 에 **이름 자체가 없다.** 한 목록 안에서
 * 두 출처가 섞이면 `Turkey` 옆에 `Türkiye` 식 표기가 같이 뜬다 → 전부 Intl 로 만든다.
 *
 * ## 왜 「그 나라 말」이 아니라 영어인가 (작성자 확정 2026-09-29)
 *
 * Intl 로 현지어(endonym)를 만들면 208개국 전부 나오지만 아랍어·히브리어·키릴·데바나가리가
 * 목록에 섞이고(`المغرب` · `ישראל` · `Црна Гора`) 우→좌 문자가 행 모양을 흐트러뜨린다.
 * 목록의 A~Z 묶음도 영어 첫 글자 기준이다. 영어면 셋이 한 번에 맞는다.
 *
 * ⚠️ **검색은 좁히지 않는다** — 한국어·현지어·옛 이름으로도 찾게 `searchNames` 가 전부 모은다.
 */

/**
 * Intl 영어 표기를 **덮어쓰는** 자리. 늘릴 때는 이유를 같이 적는다.
 *
 * Intl(CLDR)은 `Hong Kong SAR China` · `Macao SAR China` 로 길게 적는다. 목록 행이 좁고
 * 지금까지 `Hong Kong` · `Macau` 로 보여 왔다.
 */
const ENGLISH_OVERRIDES: Readonly<Record<string, string>> = {
  HK: "Hong Kong",
  MO: "Macau",
};

/**
 * 이름에 안 들어 있는 흔한 검색어. 종전엔 `getShippingCountries.ts` 에 있어 **mgmt 경로에만**
 * 붙었다 — GE 목록은 그 경로를 거치지 않아 GE 모드에서 `uk` 로 영국을 못 찾게 된다(#5839).
 * 두 경로가 모두 `countrySearchText` 를 부르므로 여기 둔다.
 */
export const COUNTRY_ALIASES: Readonly<Partial<Record<string, string[]>>> = {
  KR: ["south korea"],
  US: ["america"],
  GB: ["uk", "united kingdom", "britain"],
};

let englishNames: Intl.DisplayNames | null | undefined;
let koreanNames: Intl.DisplayNames | null | undefined;

/** 만들지 못하는 런타임이면 `null` — 호출처는 코드로 떨어진다. */
function displayNames(locale: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    return null;
  }
}

function nameIn(dn: Intl.DisplayNames | null, cc: string): string | undefined {
  try {
    const name = dn?.of(cc);
    // 모르는 코드면 Intl 이 코드를 그대로 돌려준다 — 이름이 아니다.
    return name && name !== cc ? name : undefined;
  } catch {
    return undefined;
  }
}

/** 행에 보여 줄 이름. 만들 수 없으면 국가 코드. */
export function countryDisplayName(countryCode: string): string {
  const cc = countryCode.toUpperCase();
  if (ENGLISH_OVERRIDES[cc]) return ENGLISH_OVERRIDES[cc];
  englishNames ??= displayNames("en");
  return nameIn(englishNames, cc) ?? cc;
}

/** 한국어 이름. 검색·`koreanCountryName` 용. */
export function countryKoreanName(countryCode: string): string {
  const cc = countryCode.toUpperCase();
  koreanNames ??= displayNames("ko");
  return nameIn(koreanNames, cc) ?? countryDisplayName(cc);
}

/** 그 나라의 대표 언어로 쓴 이름(예: `Deutschland`). 검색 전용 — 표시에는 쓰지 않는다. */
function countryEndonym(cc: string): string | undefined {
  try {
    const lang = new Intl.Locale(`und-${cc}`).maximize().language;
    return nameIn(displayNames(lang), cc);
  } catch {
    return undefined;
  }
}

/**
 * 검색어로 받아 줄 이름 전부를 소문자로 이어 붙인다.
 *
 * `extra` 에는 mgmt 가 주던 옛 이름(`Turkey` · `Czech Republic` · `터키` · `Deutschland`)을
 * 넘긴다 — 표시는 Intl 로 바뀌어도 **예전 이름으로 찾던 사용자가 못 찾게 되지 않도록.**
 */
export function countrySearchText(
  countryCode: string,
  extra: readonly (string | null | undefined)[] = [],
): string {
  const cc = countryCode.toUpperCase();
  const names = [
    countryDisplayName(cc),
    nameIn((englishNames ??= displayNames("en")), cc),
    countryKoreanName(cc),
    countryEndonym(cc),
    cc,
    ...(COUNTRY_ALIASES[cc] ?? []),
    ...extra,
  ];
  return [...new Set(names.filter((n): n is string => !!n))]
    .join(" ")
    .toLowerCase();
}
